import {Subscription} from "rxjs";
import {Router, RouterLink} from "@angular/router";
import {Component, inject, Input, OnDestroy, OnInit} from '@angular/core';

import {ToastrService} from "ngx-toastr";
import {NgbModalRef} from "@ng-bootstrap/ng-bootstrap";

import {appRoutes} from "@numizmat/constants";
import {AdStatus} from "@numizmat/enumerations";
import {AdApiService} from "@numizmat/services/core";
import {AdRenewalIndividualModalComponent} from "@numizmat/modals";
import {AlertService, ModalService} from "@numizmat/services/common";
import {PublicAdDetailResponse} from "@numizmat/services-models/core";
import {AdDetailStateService} from "@numizmat/services/core/ad-detail/ad-detail-state.service";
import {
  RenewalHistoryModalComponent
} from "@numizmat/pages/ad-detail-page/modals/renewal-history-modal/renewal-history-modal.component";

@Component({
  selector: 'nz-actions-panel',
  standalone: true,
  imports: [
    RouterLink,
  ],
  templateUrl: './actions-panel.component.html',
  styleUrl: './actions-panel.component.scss'
})
export class ActionsPanelComponent implements OnInit, OnDestroy {
  @Input() adId!: string;

  private router: Router = inject(Router);
  private modalService: ModalService = inject(ModalService);
  private alertService: AlertService = inject(AlertService);
  private adApiService: AdApiService = inject(AdApiService);
  private toastrService: ToastrService = inject(ToastrService);
  private adDetailStateService: AdDetailStateService = inject(AdDetailStateService);

  private subs: Subscription = new Subscription();

  protected loading: boolean = false;
  protected adDetails!: PublicAdDetailResponse;

  protected readonly AdStatus = AdStatus;
  protected readonly appRoutes = appRoutes;

  public ngOnInit(): void {
    this.getPublicAdDetails();
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  protected showStopAdWarning(): void {
    this.alertService.showStopAdWarning(this.adDetails.title).then((isConfirmed) => {
      if (isConfirmed) {
        this.loading = true;
        this.adApiService.stopAd(this.adId).subscribe({
          next: response => {
            this.loading = false;
            if (response.success) {
              this.toastrService.success(response.successMessage);
            } else {
              this.toastrService.error(response.errorMessage);
            }
          }
        });
      }
    });
  }

  protected showDeleteAdWarning(): void {
    this.alertService.showDeleteAdWarning(this.adDetails.title).then((isConfirmed) => {
      if (isConfirmed) {
        this.loading = true;
        this.adApiService.deleteAd(this.adId).subscribe({
          next: response => {
            this.loading = false;
            if (response.success) {
              this.toastrService.success(response.successMessage);
            } else {
              this.toastrService.error(response.errorMessage);
            }
          }
        });
      }
    });
  }

  protected onOpenAdRenewalIndividualModal(mode: 'after-sold' | 'after-expired'): void {
    const modalRef: NgbModalRef = this.modalService.open(AdRenewalIndividualModalComponent, {
      scrollable: true,
      modalDialogClass: 'full-height-modal'
    });
    modalRef.componentInstance.mode = mode;
    modalRef.componentInstance.adId = this.adDetails.adId;
    modalRef.componentInstance.adTitle = this.adDetails.title;
    const subscription: Subscription = modalRef.componentInstance.reloadData.subscribe({
      next: (reloadData: { success: boolean, adIds: string[] }) => {
        if (reloadData.success) {
          if (mode == 'after-sold') {
            setTimeout(() => {
              this.router.navigate([appRoutes.root, appRoutes.ad, reloadData.adIds[0]]).then();
            }, 100);
          }
        }
      }
    });
    this.subs.add(subscription);
  }

  protected onOpenRenewalHistoryModal(): void {
    const modalRef: NgbModalRef = this.modalService.open(RenewalHistoryModalComponent, {
      scrollable: true
    });
    modalRef.componentInstance.adId = this.adDetails.adId;
    modalRef.componentInstance.adTitle = this.adDetails.title;
  }

  private getPublicAdDetails(): void {
    const subscription: Subscription = this.adDetailStateService.getPublicAdDetails().subscribe({
      next: (adDetails: PublicAdDetailResponse | undefined) => {
        if (!!adDetails) {
          this.adDetails = adDetails;
        }
      }
    });
    this.subs.add(subscription);
  }
}
